import { type ServiceMethod } from '@/application/interfaces/service';

export type CashbackCounter = {
  id: string;
  type: {
    value: string;
    label: string;
  };
  value: number;
  earnValue?: number;
  expirationDate?: string;
};

export type CashbackReward = {
  id: string;
  status?: string;
  modelCode?: string;
  modelLabel?: string;
  description?: string;
  shortLabel?: string;
  value?: number;
  validityEndDate?: string;
  businessId?: string;
};

type RawResponse = {
  data: {
    counters?: Array<{
      id?: string;
      type?: { value?: string; label?: string };
      value?: number;
      earnValue?: number;
      expirationDate?: string;
    }>;
    rewards?: Array<{
      id?: string;
      status?: string;
      modelCode?: string;
      modelLabel?: string;
      additionnalData?: {
        description?: string;
        shortLabel?: string;
      };
      attributes?: {
        value?: number;
        validityEndDate?: string;
        businessId?: string | number;
      };
    }>;
  };
};

export type Response = {
  data: {
    counters: CashbackCounter[];
    rewards: CashbackReward[];
  };
};

const normalizeCashbackResponse = (rawResponse: RawResponse): Response => {
  const rawCounters = rawResponse?.data?.counters ?? [];
  const rawRewards = rawResponse?.data?.rewards ?? [];

  const normalizedCounters: CashbackCounter[] = rawCounters.map(
    (rawCounter) => ({
      id: String(rawCounter?.id ?? ''),
      type: {
        value: String(rawCounter?.type?.value ?? ''),
        label: String(rawCounter?.type?.label ?? ''),
      },
      value: Number(rawCounter?.value ?? 0),
      earnValue: rawCounter?.earnValue,
      expirationDate: rawCounter?.expirationDate,
    })
  );

  const normalizedRewards: CashbackReward[] = rawRewards.map((rawReward) => {
    const businessIdRaw = rawReward?.attributes?.businessId;

    return {
      id: String(rawReward?.id ?? ''),
      status: rawReward.status,
      modelCode: rawReward.modelCode,
      modelLabel: rawReward.modelLabel,
      description: rawReward?.additionnalData?.description,
      shortLabel: rawReward?.additionnalData?.shortLabel,
      value: rawReward?.attributes?.value,
      validityEndDate: rawReward?.attributes?.validityEndDate,
      businessId: String(businessIdRaw),
    };
  });

  return {
    data: {
      counters: normalizedCounters,
      rewards: normalizedRewards,
    },
  };
};

const getCartCashbacks: ServiceMethod<Response, void> =
  (httpClient) => async (_, options) => {
    const { body } = await httpClient.get<RawResponse>(
      '/api/v3/carts/cashbacks',
      { ...options }
    );

    return normalizeCashbackResponse(body);
  };

export default getCartCashbacks;
